<template>
  <div class="w-full h-full flex flex-col">
    <MidiAudioSetupDialog
      :is-visible="showMidiSetupDialog && sheetMusic.isLoaded.value"
    />
    <!-- Back button (mobile only) -->
    <IconButton
      v-if="isMobileDevice"
      class="absolute top-4 right-4 z-30 chrome-fade"
      :class="{ 'chrome-fade--hidden': !chrome.visible.value }"
      variant="text"
      size="sm"
      aria-label="Go back"
      @click="handleExitGameSession"
    >
      <ChevronLeftIcon class="w-5 h-5 text-text-tertiary" />
    </IconButton>
    <!-- Countdown overlay -->
    <ClassroomCountdownOverlay
      v-if="showCountdown"
      :seconds-remaining="countdownSeconds"
    />

    <!-- Loading state -->
    <div
      v-if="isLoading"
      class="flex-1 flex items-center justify-center"
    >
      <div class="text-center">
        <div class="w-12 h-12 border-4 border-t-primary border-surface rounded-full animate-spin mb-4 mx-auto" />
        <p class="text-text-primary text-lg">
          Loading song...
        </p>
      </div>
    </div>

    <!-- Game Area -->
    <template v-else>
      <div class="flex-1 relative min-h-0 flex flex-col">
        <GameArea
          v-model:show-settings-pane="showSettingsPane"
          v-model:show-section-selector="showSectionSelector"
          :music-xml-file="musicXmlFile"
          :audio-file="audioFile"
          :piece-tempo="selectedSong?.tempo"
          :can-seek-progress="isHost"
          :show-game-results="!isHost"
          :admin-sections="selectedSong?.sections"
          :playback-bar="isHost ? 'hidden' : 'settings-only'"
          :musical-piece-id="selectedSong?.musicalPieceId"
          :piece-name="selectedSong?.name"
          :loop-view-only="!isHost"
          :room-code="room?.code"
          @loop-update="handleLoopUpdate"
          @seek="handleSeek"
          @exit="handleExitGameSession"
          @pause="handlePause"
        >
          <template #header-left>
            <IconButton
              v-if="!isMobileDevice"
              variant="text"
              size="sm"
              aria-label="Go back"
              @click="handleExitGameSession"
            >
              <ChevronLeftIcon class="w-5 h-5 text-text-tertiary" />
            </IconButton>
          </template>
          <template #overlay-bottom-left>
            <ClassroomScoreboard
              v-if="isHost"
              :entries="liveScoreEntries"
              :show-live="showLiveScores"
              class="scoreboard-overlay-pill"
            />
          </template>
          <template #results-actions>
            <div class="text-center py-3">
              <p class="text-text-secondary text-sm">
                Waiting for host to choose next action...
              </p>
            </div>
          </template>
        </GameArea>
        <div
          class="connection-indicator-wrap chrome-fade"
          :class="{ 'chrome-fade--hidden': !chrome.visible.value }"
        >
          <ConnectionIndicator />
        </div>
        <GameControlBar
          v-if="isHost"
          v-model:show-settings-pane="showSettingsPane"
          v-model:show-section-selector="showSectionSelector"
          :is-playing="gameEngine.isPlaying.value"
          :is-preparing-start="isPreparingStart"
          :actions-disabled="showCountdown || isPreparingStart || showMidiSetupDialog"
          :musical-piece-id="selectedSong?.musicalPieceId"
          @restart="handleRestart"
          @toggle-play="gameEngine.isPlaying.value ? handlePause() : handlePlay()"
          @loop-update="handleLoopUpdate"
        />
      </div>
    </template>

    <ClassroomHostSyncOverlay v-if="isHost && isPreparingStart" />

    <!-- Results overlay -->
    <ClassroomGameResults
      v-if="showResults && isHost"
      :scores="sortedScores"
      :is-host="isHost"
      :musical-piece-id="selectedSong?.musicalPieceId"
      @play-again="handlePlayAgain"
      @back-to-lobby="handleExitGameSession"
      @close="showResults = false"
    />

    <ClassroomExitConfirmDialog
      v-model:is-visible="showExitConfirm"
      :title="exitConfirmCopy.title"
      :message="exitConfirmCopy.message"
      :confirm-label="exitConfirmCopy.confirmLabel"
      @confirm="confirmExit"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch, type Ref } from 'vue';
import { useThrottleFn } from '@vueuse/core';
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router';
import * as Sentry from '@sentry/vue';
import { useMultiplayerRoom } from '@/composables/useMultiplayerRoom';
import { provideGameEngine } from '@/composables/useGameEngine';
import { useToast } from '@/composables/useToast';
import { useDeviceDetection } from '@/composables/useDeviceDetection';
import GameArea from '@/components/GameArea.vue';
import IconButton from '@/components/IconButton.vue';
import MidiAudioSetupDialog from '@/components/MidiAudioSetupDialog.vue';
import { ChevronLeftIcon } from '@heroicons/vue/24/outline';
import ClassroomCountdownOverlay from '@/components/classroom/ClassroomCountdownOverlay.vue';
import GameControlBar from '@/components/GameControlBar.vue';
import ClassroomHostSyncOverlay from '@/components/classroom/ClassroomHostSyncOverlay.vue';
import ClassroomScoreboard from '@/components/classroom/ClassroomScoreboard.vue';
import ClassroomGameResults from '@/components/classroom/ClassroomGameResults.vue';
import ClassroomExitConfirmDialog from '@/components/ClassroomExitConfirmDialog.vue';
import { useClassroomExitConfirm } from '@/composables/useClassroomExitConfirm';
import ConnectionIndicator from '@/components/ConnectionIndicator.vue';
import { useMidiAudioSetup } from '@/composables/useMidiAudioSetup';
import { METRONOME_LOOKAHEAD_MS } from '@/constants';
import { provideSheetMusic } from '@/composables/useSheetMusic';
import { waitUntil } from '@/utils';
import type { LoopSource } from '@/types/classroom';
import { useScreenWakeLock } from '@/composables/useScreenWakeLock';
import { useSubmitPieceHighScore } from '@/composables/useSubmitPieceHighScore';
import { CLASSROOM_LOBBY_ROUTE, CLASSROOM_ROUTE_NAMES } from '@/config/multiplayer';
import { needsClassroomSongHydration } from '@/utils/classroomSongState';
import { useGameChromeVisibility } from '@/composables/useGameChromeVisibility';

const route = useRoute();
const router = useRouter();
const { createToast } = useToast();
const { isMobileDevice } = useDeviceDetection();
const sheetMusic = provideSheetMusic();
const gameEngine = provideGameEngine(sheetMusic);
const {
  showMidiSetupDialog,
  requireReadyOrPrompt,
} = useMidiAudioSetup();
useScreenWakeLock();
const chrome = useGameChromeVisibility();
// See PieceGameView.vue: leaving the game must restore the shared chrome
// singleton, or MainNav (or the next page) can be stuck hidden.
onUnmounted(() => chrome.reset());

const multiplayerRoom = useMultiplayerRoom();

const {
  room,
  participants,
  selectedSong,
  sortedScores,
  isHost,
  currentParticipant,
  isConnected,
  getWorldTime,
  syncTime,
  broadcastPlay,
  broadcastPause,
  returnToLobby,
  closeRoom,
  broadcastGameEnd,
  resetScores,
  broadcastScoreUpdate,
  submitScore,
  onPlaybackStart,
  onPlaybackPause,
  onPlaybackResume,
  onReturnToLobby,
  onGameEnd,
  onLoopSectionUpdate,
  updateLoopSection,
  updateLoopPreRollMode,
  updatePreviewMode,
  updatePlaybackSpeed,
  markSheetMusicLoaded,
  reconnectToRoom,
  leaveRoom,
} = multiplayerRoom;

const submitCurrentRunHighScore = useSubmitPieceHighScore(gameEngine);

// UI state
const isLoading = ref(true);
const musicXmlFile = ref<File>();
const audioFile = ref<File>();
const showCountdown = ref(false);
const countdownSeconds = ref(3);
const showResults = ref(false);
const showLiveScores = ref(true);
const isPreparingStart = ref(false);
const isClosingRoom = ref(false);
const isHostLeavingToNonLobby = ref(false);
const exitConfirmKind = ref<'student-leave' | 'host-return-to-lobby' | 'host-leave'>('student-leave');
const {
  showExitConfirm,
  guardExit,
  skipNextConfirm,
  confirmExit,
} = useClassroomExitConfirm(router);
const showSettingsPane = ref(false);
const showSectionSelector = ref(false);
const pendingPlaybackStart = ref<{ worldStartTime: number; songTimeMs: number } | null>(null);
let postStartResyncTimeout: ReturnType<typeof setTimeout> | null = null;
let foregroundResyncInFlight: Promise<void> | null = null;
let countdownTimer: ReturnType<typeof setTimeout> | null = null;
let seekBroadcastTimeout: ReturnType<typeof setTimeout> | null = null;
let pendingSeekBroadcastMs: number | null = null;
let lastSeekBroadcastAt = 0;
let pausedSeekLerpRaf: number | null = null;
let pausedSeekTargetMs: number | null = null;
let pausedSeekLastFrameAt = 0;
let latestSongAssetLoadRequestId = 0;
let loadedMusicalPieceId: string | null = null;

const POST_START_RESYNC_DELAY_MS = 1000;
const COUNTDOWN_TICK_MS = 100;
const PLAYBACK_RESYNC_TOLERANCE_MS = 45;
const SEEK_BROADCAST_INTERVAL_MS = 120;
const SCORE_BROADCAST_INTERVAL_MS = 1000;
const PAUSED_SEEK_LERP_FACTOR = 0.38;
const PAUSED_SEEK_DONE_THRESHOLD_MS = 1.5;
const PLAY_START_READY_TIMEOUT_MS = 8000;

const getCurrentSongTimeMs = () => Math.max(0, gameEngine.currentTime.value + METRONOME_LOOKAHEAD_MS);
const clampToPlayableSongTimeMs = (timeMs: number) => {
  const durationMs = sheetMusic.songDuration.value;
  if (!Number.isFinite(durationMs) || durationMs <= 0) {
    return Math.max(0, timeMs);
  }
  return Math.min(Math.max(0, timeMs), Math.max(0, durationMs - 1));
};

const liveScoreEntries = computed(() => {
  const scoreMap = new Map(sortedScores.value.map((score) => [score.userId, score]));
  return participants.value
    .map((participant) => ({
      userId: participant.userId,
      displayName: participant.displayName,
      score: scoreMap.get(participant.userId)?.score ?? 0,
    }))
    .sort((a, b) => b.score - a.score || a.displayName.localeCompare(b.displayName));
});

const stopLocalPlayback = () => {
  gameEngine.stopPlayback();
};

const pauseAndBroadcastAtSongTime = async (songTimeMs: number) => {
  clearPostStartResyncTimer();
  clearSeekBroadcastTimer();
  pendingSeekBroadcastMs = null;
  clearCountdownTimer();
  showCountdown.value = false;
  const pausedAtMs = pauseLocalPlaybackAtSongTime(songTimeMs);
  await broadcastPause(pausedAtMs);
};

const pauseLocalPlaybackAtSongTime = (songTimeMs: number) => {
  const clampedSongTimeMs = Math.max(0, songTimeMs);
  stopPausedSeekLerp();
  pausedSeekTargetMs = null;
  gameEngine.stopPlayback();
  gameEngine.seekTo(clampedSongTimeMs);
  return clampedSongTimeMs;
};

const clearCountdownTimer = () => {
  if (countdownTimer) {
    clearTimeout(countdownTimer);
    countdownTimer = null;
  }
};

// A countdown is only meaningful while its timer can still reach launchPlayback.
// Keep cancelling the timer and dismissing its UI inseparable: otherwise a room
// reconciliation can leave the last rendered digit (commonly "1") on screen
// indefinitely.
const cancelCountdown = () => {
  clearCountdownTimer();
  showCountdown.value = false;
};

const clearPostStartResyncTimer = () => {
  if (postStartResyncTimeout) {
    clearTimeout(postStartResyncTimeout);
    postStartResyncTimeout = null;
  }
};

const clearSeekBroadcastTimer = () => {
  if (seekBroadcastTimeout) {
    clearTimeout(seekBroadcastTimeout);
    seekBroadcastTimeout = null;
  }
};

const stopPausedSeekLerp = () => {
  if (pausedSeekLerpRaf !== null) {
    cancelAnimationFrame(pausedSeekLerpRaf);
    pausedSeekLerpRaf = null;
  }
  pausedSeekLastFrameAt = 0;
};

const stepPausedSeekLerp = () => {
  if (pausedSeekTargetMs === null) {
    stopPausedSeekLerp();
    return;
  }

  const now = performance.now();
  const deltaMs = pausedSeekLastFrameAt === 0 ? 16 : Math.min(50, now - pausedSeekLastFrameAt);
  pausedSeekLastFrameAt = now;

  const currentSongTime = getCurrentSongTimeMs();
  const drift = pausedSeekTargetMs - currentSongTime;
  const absDrift = Math.abs(drift);

  if (absDrift <= PAUSED_SEEK_DONE_THRESHOLD_MS) {
    gameEngine.seekTo(Math.max(0, pausedSeekTargetMs));
    stopPausedSeekLerp();
    return;
  }

  // True interpolation-factor blend (not constant speed).
  const frameScale = Math.min(3, deltaMs / 16.67);
  const alpha = 1 - Math.pow(1 - PAUSED_SEEK_LERP_FACTOR, frameScale);
  const nextSongTime = Math.max(0, currentSongTime + drift * alpha);

  gameEngine.seekTo(nextSongTime);
  pausedSeekLerpRaf = requestAnimationFrame(stepPausedSeekLerp);
};

const lerpToPausedSeekTarget = (targetMs: number) => {
  pausedSeekTargetMs = targetMs;
  if (pausedSeekLerpRaf === null) {
    pausedSeekLerpRaf = requestAnimationFrame(stepPausedSeekLerp);
  }
};

const flushSeekBroadcast = async () => {
  if (!isHost.value || pendingSeekBroadcastMs === null) return;
  const targetMs = pendingSeekBroadcastMs;
  pendingSeekBroadcastMs = null;
  lastSeekBroadcastAt = Date.now();
  try {
    await broadcastPause(targetMs);
  } catch (error) {
    console.warn('[Classroom] Failed to broadcast seek pause:', error);
  }
};

const queueSeekBroadcast = (targetMs: number) => {
  pendingSeekBroadcastMs = targetMs;

  const elapsed = Date.now() - lastSeekBroadcastAt;
  const waitMs = Math.max(0, SEEK_BROADCAST_INTERVAL_MS - elapsed);

  if (waitMs <= 0) {
    clearSeekBroadcastTimer();
    flushSeekBroadcast();
    return;
  }

  if (!seekBroadcastTimeout) {
    seekBroadcastTimeout = setTimeout(() => {
      seekBroadcastTimeout = null;
      flushSeekBroadcast();
    }, waitMs);
  }
};

// The room's status is the only authority on where a client belongs: "waiting"
// means no run is on screen — a finished run holds the room at "ended" instead —
// so the host is sitting in the lobby and every client belongs there too.
const belongsInLobby = () => room.value?.status === 'waiting';

const navigateToLobby = (message?: string, { skipExitConfirm = true } = {}) => {
  if (skipExitConfirm) skipNextConfirm();
  if (message) {
    createToast({ type: 'info', message });
  }
  router.push({ name: 'classroom-lobby', params: { code: route.params.code } });
};

const navigateToClassroom = (message?: string, { skipExitConfirm = true } = {}) => {
  if (skipExitConfirm) skipNextConfirm();
  if (message) {
    createToast({ type: 'error', message });
  }
  router.push({ name: 'classroom' });
};

const isRoomNotFoundError = (error: unknown): boolean => {
  if (!(error instanceof Error)) return false;
  return error.message.toLowerCase().includes('room not found');
};

const handleRoomUnavailable = (message = 'Classroom is no longer available') => {
  if (isHost.value && isHostLeavingToNonLobby.value) return;
  clearCountdownTimer();
  clearPostStartResyncTimer();
  clearSeekBroadcastTimer();
  pendingSeekBroadcastMs = null;
  stopPausedSeekLerp();
  pausedSeekTargetMs = null;
  stopLocalPlayback();
  showCountdown.value = false;
  showResults.value = false;
  showMidiSetupDialog.value = false;
  navigateToClassroom(message);
};

const closeRoomAsHost = async () => {
  if (!isHost.value || !room.value || isClosingRoom.value) return;
  isClosingRoom.value = true;
  try {
    await closeRoom();
  } catch (error) {
    console.error('Failed to close room:', error);
    createToast({ type: 'error', message: 'Unable to close room right now' });
  } finally {
    isClosingRoom.value = false;
  }
};

const queuePlaybackStartIfNeeded = (worldStartTime: number, songTimeMs: number) => {
  showResults.value = false;
  if (showMidiSetupDialog.value) {
    pendingPlaybackStart.value = { worldStartTime, songTimeMs };
    showMidiSetupDialog.value = true;
    return;
  }
  startCountdown(worldStartTime, songTimeMs);
};

// Host-controlled engine state that the room mirrors. Both directions guard on
// equality first: a no-op write would re-fire the watcher on the other side and
// bounce the value straight back (see agent.md § two-way sync).
const pullHostSetting = <T,>(local: Ref<T>, remote: T) => {
  if (local.value !== remote) local.value = remote;
};

const pushHostSetting = <T,>(
  local: () => T,
  remote: () => T,
  push: (value: T) => Promise<void>,
  label: string,
  isEqual: (a: T, b: T) => boolean = Object.is,
) =>
  watch(local, (value) => {
    if (!isHost.value) return;
    if (isEqual(value, remote())) return;
    push(value).catch((error) => {
      console.error(`[Classroom] Failed to broadcast ${label}:`, error);
      createToast({ type: 'error', message: `Failed to update ${label}` });
    });
  });

// Keep local loop state in sync with room state (including late hydration/reconnect).
watch(
  room,
  (nextRoom) => {
    if (isHost.value && isHostLeavingToNonLobby.value) {
      return;
    }

    if (!nextRoom) {
      if (!isLoading.value) {
        handleRoomUnavailable();
      }
      return;
    }

    if (nextRoom.status === 'finished') {
      handleRoomUnavailable('Host left the game. Room has been closed.');
      return;
    }

    gameEngine.loopStartMeasure.value = nextRoom.loopStartMeasure ?? null;
    gameEngine.loopEndMeasure.value = nextRoom.loopEndMeasure ?? null;
    gameEngine.loopSource.value = nextRoom.loopSource;

    // Preview mode, playback speed and the loop lead-in mode are all
    // host-controlled and broadcast through room state — pull the authoritative
    // values into the local engine.
    pullHostSetting(gameEngine.previewMode, nextRoom.previewMode ?? false);
    pullHostSetting(gameEngine.playbackSpeed, nextRoom.playbackSpeed ?? 1);
    pullHostSetting(gameEngine.loopPreRollEveryPass, nextRoom.loopPreRollEveryPass ?? false);

    // Authoritative room-state redirect — the host having sent everyone back,
    // this client having outlived a run, or a /play URL typed straight into a
    // room whose host is still in the lobby. Deliberately not gated on the load
    // finishing: the snapshot a reconnect delivers lands while the song is still
    // downloading, and nothing re-reads the status afterwards.
    if (belongsInLobby()) {
      navigateToLobby();
    }
  },
  { immediate: true }
);

// The speed slider steps by 0.05, so compare with a float tolerance.
pushHostSetting(
  () => gameEngine.playbackSpeed.value,
  () => room.value?.playbackSpeed ?? 1,
  updatePlaybackSpeed,
  'playback speed',
  (a, b) => Math.abs(a - b) < 1e-6,
);
pushHostSetting(
  () => gameEngine.previewMode.value,
  () => room.value?.previewMode ?? false,
  updatePreviewMode,
  'preview mode',
);
pushHostSetting(
  () => gameEngine.loopPreRollEveryPass.value,
  () => room.value?.loopPreRollEveryPass ?? false,
  updateLoopPreRollMode,
  'loop lead-in mode',
);

// The room anchors playback as a world-clock start time plus the song position
// it started from, but the game clock advances at `playbackSpeed × real elapsed`
// (see gameLoop in useGameEngine), so raw wall-clock ms overshoot by
// `elapsed × (1 - speed)` — half a second per second of playback at 0.5×.
// Speed comes from the room: that's what the host's timeline was built on. One
// constant speed covers the whole window because changing it requires a pause
// (the slider is disabled while playing) and pause/resume re-anchor the timeline.
const projectSongTimeMs = (anchorSongTimeMs: number, worldStartTime: number) => {
  const elapsedRealMs = Math.max(0, getWorldTime() - worldStartTime);
  return anchorSongTimeMs + elapsedRealMs * (room.value?.playbackSpeed ?? 1);
};

const getExpectedRoomSongTimeMs = () => {
  const currentRoom = room.value;
  if (!currentRoom) return null;
  if (currentRoom.status !== 'playing' || currentRoom.scheduledStartTime === undefined) return null;
  return projectSongTimeMs(currentRoom.currentTimeMs, currentRoom.scheduledStartTime);
};

const resyncPlaybackToRoom = (reason: 'post-start' | 'foreground') => {
  // Host drives authoritative playback state and should not self-correct from
  // room snapshots; doing so can create visible jumps around loop boundaries.
  if (isHost.value) return;

  const expectedSongTime = getExpectedRoomSongTimeMs();
  if (expectedSongTime === null) return;
  if (showCountdown.value || showResults.value) return;

  if (!gameEngine.isPlaying.value) {
    queuePlaybackStartIfNeeded(room.value!.scheduledStartTime!, room.value!.currentTimeMs);
    return;
  }

  // A client at slowed tempo sits one WSOLA window behind the room's ideal
  // timeline (see getLaunchLatencyHoldSongMs), so being in sync looks like that
  // offset rather than like zero — without it every slowed mid-track start reads
  // past the tolerance below. Keyed on the room's launch position rather than our
  // own hold, so it yields what the host is running with: a student who joined
  // mid-run holds where the host didn't, and belongs pulled to the host.
  const targetSongTime = expectedSongTime
    - gameEngine.getLaunchLatencyHoldSongMs(room.value!.currentTimeMs);
  const driftMs = targetSongTime - getCurrentSongTimeMs();
  if (Math.abs(driftMs) < PLAYBACK_RESYNC_TOLERANCE_MS) return;

  console.info(`[Classroom] Resyncing playback (${reason}), drift=${Math.round(driftMs)}ms`);
  // Unlike every other seek here this one runs mid-playback, which decides both
  // halves of the call: the audio has to move with the clock or the desync
  // corrector undoes it, and `seekTo` lands the clock on its argument instead of
  // parking it a lookahead early, so the target is clock time, not song time.
  gameEngine.seekWithAudio(targetSongTime - METRONOME_LOOKAHEAD_MS);
};

const schedulePostStartResync = () => {
  clearPostStartResyncTimer();
  postStartResyncTimeout = setTimeout(() => {
    postStartResyncTimeout = null;
    resyncPlaybackToRoom('post-start');
  }, POST_START_RESYNC_DELAY_MS);
};

const syncFromCurrentRoomSnapshot = () => {
  const currentRoom = room.value;
  if (!currentRoom) return;

  if (currentRoom.status === 'playing' && currentRoom.scheduledStartTime !== undefined) {
    if (showResults.value) showResults.value = false;
    // A backgrounded webview can defer the final countdown timeout. Rebuild the
    // countdown from the server-clock deadline; when it is already past this
    // launches immediately. This also replaces any stale local timer.
    if (showCountdown.value && !gameEngine.isPlaying.value) {
      queuePlaybackStartIfNeeded(currentRoom.scheduledStartTime, currentRoom.currentTimeMs);
      return;
    }
    resyncPlaybackToRoom('foreground');
    return;
  }

  if (currentRoom.status === 'paused') {
    cancelCountdown();
    clearPostStartResyncTimer();
    pauseLocalPlaybackAtSongTime(currentRoom.currentTimeMs);
  }
};

const handleAppForegroundSync = async () => {
  if (isLoading.value) return;
  if (foregroundResyncInFlight) return foregroundResyncInFlight;

  foregroundResyncInFlight = (async () => {
    try {
      await syncTime();
    } catch (error) {
      console.warn('[Classroom] Foreground syncTime failed:', error);
    }

    const urlCode = String(route.params.code ?? '').toUpperCase();
    if (!room.value && urlCode) {
      try {
        await reconnectToRoom(urlCode);
      } catch (error) {
        if (isRoomNotFoundError(error)) {
          handleRoomUnavailable();
          return;
        }
        console.warn('[Classroom] Foreground reconnect failed:', error);
      }
    }

    syncFromCurrentRoomSnapshot();
  })();

  try {
    await foregroundResyncInFlight;
  } finally {
    foregroundResyncInFlight = null;
  }
};

const onVisibilityChange = () => {
  if (document.visibilityState === 'visible') {
    handleAppForegroundSync();
  }
};

type SongAssetSource = {
  musicalPieceId: string;
  sheetMusicUrl: string;
  sheetMusicFilename: string;
  audioUrl?: string;
  audioFilename?: string;
  audioOffsetMs?: number;
};

const loadSongAssets = async (song: SongAssetSource) => {
  const requestId = ++latestSongAssetLoadRequestId;
  isLoading.value = true;

  const isCurrentRequest = () =>
    requestId === latestSongAssetLoadRequestId &&
    room.value?.musicalPieceId === song.musicalPieceId &&
    selectedSong.value?.musicalPieceId === song.musicalPieceId;

  try {
    const musicXmlResponse = await fetch(song.sheetMusicUrl);
    if (!musicXmlResponse.ok) throw new Error('Failed to download sheet music');
    const musicXmlBlob = await musicXmlResponse.blob();
    const nextMusicXmlFile = new File(
      [musicXmlBlob],
      song.sheetMusicFilename,
      { type: 'text/xml' }
    );

    let nextAudioFile: File | undefined;
    if (song.audioUrl) {
      const audioResponse = await fetch(song.audioUrl);
      if (audioResponse.ok) {
        const audioBlob = await audioResponse.blob();
        nextAudioFile = new File(
          [audioBlob],
          song.audioFilename ?? 'audio.mp3',
          { type: 'audio/mpeg' }
        );
      }
    }

    if (!isCurrentRequest()) return;

    musicXmlFile.value = nextMusicXmlFile;
    audioFile.value = nextAudioFile;
    gameEngine.audioOffset.value = song.audioOffsetMs ?? 0;
    loadedMusicalPieceId = song.musicalPieceId;
    isLoading.value = false;

    // If a participant refreshes mid-game (room already "playing"), re-sync to the
    // current playback position so they don't miss the game in progress.
    if (room.value?.status === 'playing' && room.value.scheduledStartTime !== undefined) {
      queuePlaybackStartIfNeeded(room.value.scheduledStartTime, room.value.currentTimeMs);
    }
  } catch (err) {
    if (!isCurrentRequest()) return;
    console.error('Failed to load song:', err);
    createToast({ type: 'error', message: 'Failed to load song' });
    router.push({ name: 'classroom-lobby', params: { code: route.params.code } });
  }
};

watch(
  [() => room.value?.musicalPieceId, () => selectedSong.value?.musicalPieceId],
  ([roomPieceId, selectedSongPieceId]) => {
    const song = selectedSong.value;
    if (!roomPieceId || roomPieceId !== selectedSongPieceId || !song) return;
    if (loadedMusicalPieceId === roomPieceId) return;
    void loadSongAssets(song);
  }
);

// Load song files
onMounted(async () => {
  const urlCode = String(route.params.code ?? '').toUpperCase();

  if (!urlCode) {
    createToast({ type: 'error', message: 'Invalid room code' });
    router.push({ name: 'classroom' });
    return;
  }

  // `isConnected` and not just `room`: teardown leaves the last snapshot in
  // place, so a populated room can outlive the subscriptions behind it.
  if (needsClassroomSongHydration({
    room: room.value,
    roomCode: urlCode,
    selectedSong: selectedSong.value,
    isConnected: isConnected.value,
  })) {
    try {
      await reconnectToRoom(urlCode);
    } catch (err) {
      if (isRoomNotFoundError(err)) {
        handleRoomUnavailable();
        return;
      }
      const message = err instanceof Error ? err.message : 'Failed to reconnect to room';
      createToast({ type: 'error', message });
      router.push({ name: 'classroom-lobby', params: { code: urlCode } });
      return;
    }
  }

  // A client the room places in the lobby is already on its way there, sent by
  // the watcher above. Stop before downloading a song this client won't play, and
  // before the "No song selected" complaint below — the wrong thing to say to a
  // student who typed the /play URL while the host was still choosing.
  if (belongsInLobby()) return;

  if (!selectedSong.value || selectedSong.value.musicalPieceId !== room.value?.musicalPieceId) {
    createToast({ type: 'error', message: 'No song selected' });
    router.push({ name: 'classroom-lobby', params: { code: urlCode } });
    return;
  }

  document.addEventListener('visibilitychange', onVisibilityChange, { passive: true });
  window.addEventListener('pageshow', handleAppForegroundSync, { passive: true });
  window.addEventListener('focus', handleAppForegroundSync, { passive: true });

  await loadSongAssets(selectedSong.value);
});

const offPlaybackStart = onPlaybackStart(({ scheduledTime, songTimeMs }) => {
  stopPausedSeekLerp();
  pausedSeekTargetMs = null;
  queuePlaybackStartIfNeeded(scheduledTime, songTimeMs);
});

const offPlaybackPause = onPlaybackPause(({ pausedAtMs }) => {
  clearPostStartResyncTimer();
  clearCountdownTimer();
  showCountdown.value = false;

  if (isHost.value) {
    return;
  }

  const wasPlaying = gameEngine.isPlaying.value;
  const clampedPausedAtMs = Math.max(0, pausedAtMs);

  if (wasPlaying) {
    pauseLocalPlaybackAtSongTime(clampedPausedAtMs);
    createToast({ type: 'info', message: 'Paused' });
    return;
  }

  stopLocalPlayback();
  // Smooth only paused seek-sync updates while already paused.
  lerpToPausedSeekTarget(clampedPausedAtMs);
});

const offPlaybackResume = onPlaybackResume(({ scheduledTime, resumeAtMs }) => {
  stopPausedSeekLerp();
  pausedSeekTargetMs = null;
  queuePlaybackStartIfNeeded(scheduledTime, resumeAtMs);
});

const offReturnToLobby = onReturnToLobby(() => {
  clearPostStartResyncTimer();
  clearSeekBroadcastTimer();
  pendingSeekBroadcastMs = null;
  stopPausedSeekLerp();
  pausedSeekTargetMs = null;
  stopLocalPlayback();
  gameEngine.initGame();
  clearCountdownTimer();
  navigateToLobby('Host returned to lobby');
});

const offGameEnd = onGameEnd(() => {
  clearPostStartResyncTimer();
  clearSeekBroadcastTimer();
  pendingSeekBroadcastMs = null;
  showResults.value = true;
});

// Start countdown to scheduled time (safe to call while already playing — handles restart)
const startCountdown = (worldStartTime: number, songTimeMs: number) => {
  showResults.value = false;

  // Always cancel any in-progress countdown and stop current playback so restart works
  clearCountdownTimer();
  gameEngine.stopPlayback();
  // Fresh start wipes the run; resume rewinds to songTimeMs — notes at/after it
  // go back to unplayed and their score is handed back, so replaying that
  // stretch scores it once instead of stacking onto the previous pass.
  if (songTimeMs === 0) {
    gameEngine.initGame();
  } else {
    gameEngine.revertNoteHitsAtOrAfter(songTimeMs);
  }

  // Launch from the timeline position that corresponds to actual launch time.
  // This compensates for event-loop jitter and keeps all clients closer to server time.
  const launchPlayback = () => {
    const effectiveSongTimeMs = clampToPlayableSongTimeMs(projectSongTimeMs(songTimeMs, worldStartTime));

    showCountdown.value = false;
    gameEngine.seekTo(effectiveSongTimeMs);
    gameEngine.startGame().catch((error) => {
      console.error('[Classroom] Failed to launch synchronized playback:', error);
      Sentry.captureException(error, {
        tags: {
          feature: 'classroom',
          operation: 'synchronized-playback-launch',
        },
        extra: {
          roomCode: room.value?.code,
          isHost: isHost.value,
          scheduledStartTime: worldStartTime,
          launchSongTimeMs: effectiveSongTimeMs,
        },
      });
      createToast({ type: 'error', message: 'Unable to start playback. Please try again.' });
    });
    if (!isHost.value) {
      schedulePostStartResync();
    }
  };

  // One self-rescheduling timer drives both the digit and the launch, each tick
  // re-derived from getWorldTime(), so a clock resync landing mid-countdown (the
  // heartbeat feeds one every few seconds) corrects the number shown and the
  // launch moment together, with nothing to reschedule. The delay cap keeps the
  // digit live; the final tick still lands exactly on `remaining`.
  const tick = () => {
    countdownTimer = null;
    const remaining = Math.max(0, worldStartTime - getWorldTime());
    countdownSeconds.value = Math.ceil(remaining / 1000);

    if (remaining <= 0) {
      launchPlayback();
      return;
    }

    countdownTimer = setTimeout(tick, Math.min(remaining, COUNTDOWN_TICK_MS));
  };

  showCountdown.value = true;
  tick();
};

// Watch for game progress to broadcast scores
watch([gameEngine.progress, gameEngine.isPlaying], ([progress, isPlaying]) => {
  if (progress >= 100 && !isPlaying) {
    // Submit final score
    if (room.value && selectedSong.value) {
      submitScore({
        score: gameEngine.score.value,
        accuracy: gameEngine.accuracy.value,
        perfectCount: gameEngine.perfectCount.value,
        goodCount: gameEngine.goodCount.value,
        missedCount: gameEngine.missedCount.value,
        longestCombo: gameEngine.longestCombo.value,
        totalNotes: gameEngine.totalNotes.value,
      });
    }

    // Submit personal highscore for this piece (host AND students). The
    // mutation is idempotent — a duplicate submit from GameAreaHighScoreRow
    // for students with the same score is a no-op.
    const pieceId = selectedSong.value?.musicalPieceId;
    if (pieceId) submitCurrentRunHighScore(pieceId);

    // Host broadcasts game end
    if (isHost.value) {
      broadcastGameEnd();
    }
  }
});

// Push score changes so the teacher's scoreboard matches what the player sees,
// including when a loop rewind hands points back. Throttled with a trailing call
// so a burst of hits costs one mutation and the settled value still lands, and
// gated on a live session so initGame()'s wipe can't overwrite a stored result.
const liveScorePayload = computed(() => ({
  score: gameEngine.score.value,
  accuracy: gameEngine.accuracy.value,
  comboCount: gameEngine.comboCount.value,
  perfectCount: gameEngine.perfectCount.value,
  goodCount: gameEngine.goodCount.value,
  missedCount: gameEngine.missedCount.value,
  totalNotes: gameEngine.totalNotes.value,
}));

const broadcastLiveScore = useThrottleFn(async () => {
  const status = room.value?.status;
  if (status !== 'playing' && status !== 'paused') return;
  try {
    await broadcastScoreUpdate(liveScorePayload.value);
  } catch (error) {
    console.warn('[Classroom] Failed to broadcast score update:', error);
  }
}, SCORE_BROADCAST_INTERVAL_MS, true);

watch(liveScorePayload, broadcastLiveScore);

const offLoopSectionUpdate = onLoopSectionUpdate((loopSettings) => {
  gameEngine.loopStartMeasure.value = loopSettings.startMeasure;
  gameEngine.loopEndMeasure.value = loopSettings.endMeasure;
  gameEngine.loopSource.value = loopSettings.source;
});

// Events and conditions
// Everyone listed is present — the presence sweep drops anyone who goes quiet —
// so a student still short of their sheet music is one the host should wait for.
const areAllStudentsReady = computed(() => {
  return !participants.value.some(
    (p) => p.role === 'student' && !p.hasSheetMusicLoaded
  );
});

// Host controls
const runHostBroadcastStart = async (
  startAtMs: number,
  options?: { requiresReadyPrompt?: boolean; freshRun?: boolean }
) => {
  if (options?.requiresReadyPrompt && !requireReadyOrPrompt()) {
    return;
  }
  if (isPreparingStart.value) return;
  isPreparingStart.value = true;
  clearSeekBroadcastTimer();
  pendingSeekBroadcastMs = null;
  const freshRun = options?.freshRun ?? false;
  if (freshRun) {
    clearCountdownTimer();
    showCountdown.value = false;
    gameEngine.initGame();
  }
  const effectiveStartAtMs = gameEngine.getLoopPreRollStartMs() ?? Math.max(0, startAtMs);
  const clampedStartAtMs = clampToPlayableSongTimeMs(effectiveStartAtMs);
  gameEngine.stopPlayback();
  gameEngine.seekTo(clampedStartAtMs);
  try {
    if (!areAllStudentsReady.value) {
      await waitUntil(() => areAllStudentsReady.value, PLAY_START_READY_TIMEOUT_MS);
    }
    // Clear immediately before starting, never after: clients push live scores
    // while the room sits at "paused", and a push landing between the two calls
    // would survive a reset that ran second.
    if (freshRun) {
      await resetScores();
    }
    await broadcastPlay(clampedStartAtMs);
  } catch (error) {
    console.error('Failed to start synchronized playback:', error);
    createToast({ type: 'error', message: 'Unable to sync players. Please try again.' });
  } finally {
    isPreparingStart.value = false;
  }
};

const handlePlay = async () => {
  // Starting from the top is a fresh run even when a loop pre-roll shifts the
  // time actually broadcast off zero.
  const startAtMs = getCurrentSongTimeMs();
  await runHostBroadcastStart(startAtMs, {
    requiresReadyPrompt: true,
    freshRun: startAtMs === 0,
  });
};

const handlePause = async () => {
  clearPostStartResyncTimer();
  clearSeekBroadcastTimer();
  // A queued scrub position is stale once the host pauses at the live clock.
  pendingSeekBroadcastMs = null;
  stopLocalPlayback();
  const pausedAtMs = getCurrentSongTimeMs();
  await broadcastPause(pausedAtMs);
};

const handleRestart = async () => {
  await runHostBroadcastStart(0, {
    requiresReadyPrompt: true,
    freshRun: true,
  });
};

const handlePlayAgain = async () => {
  showResults.value = false;
  if (!isHost.value) {
    gameEngine.initGame();
    return;
  }
  await runHostBroadcastStart(0, { freshRun: true });
};

const handleSeek = ({ timeMs, wasPlaying }: { timeMs: number; wasPlaying: boolean }) => {
  if (!isHost.value) return;
  clearPostStartResyncTimer();
  clearCountdownTimer();
  showCountdown.value = false;
  if (wasPlaying || gameEngine.isPlaying.value) {
    gameEngine.stopPlayback();
  }
  queueSeekBroadcast(Math.max(0, timeMs));
};

// Announce readiness whenever the server doesn't already have it, rather than
// once on load: a participant re-admitted mid-session (or re-joining, which
// resets the flag) comes back with it cleared, and a host waiting on a stale
// `false` burns the full ready timeout before every start.
watch(
  [sheetMusic.isLoaded, () => currentParticipant.value?.hasSheetMusicLoaded, isHost],
  async ([sheetLoaded, serverHasIt, host]) => {
    if (host || !sheetLoaded || serverHasIt || !room.value) return;
    try {
      await markSheetMusicLoaded();
    } catch (error) {
      console.warn('[Classroom] Failed to mark sheet music loaded:', error);
    }
  },
  { immediate: true }
);

const exitConfirmCopy = computed(() => {
  switch (exitConfirmKind.value) {
    case 'host-return-to-lobby':
      return {
        title: 'Return to Lobby?',
        message: 'Going back will end the current song for everyone and return to the lobby. Continue?',
        confirmLabel: 'Return to Lobby',
      };
    case 'host-leave':
      return {
        title: 'Leave Classroom?',
        message: 'Leaving will close the classroom for everyone. Are you sure you want to exit?',
        confirmLabel: 'Leave',
      };
    default:
      return {
        title: 'Leave Classroom?',
        message: 'Are you sure you want to leave the classroom?',
        confirmLabel: 'Leave',
      };
  }
});

const handleExitGameSession = () => {
  if (isHost.value) {
    // Putting the room back to "waiting" is the leave guard's job, so that every
    // exit writes it — this one, the results overlay, browser and hardware back.
    navigateToLobby(undefined, { skipExitConfirm: false });
    return;
  }
  navigateToClassroom(undefined, { skipExitConfirm: false });
};

// Leaving the classroom is what ends this client's session — the host closes
// the room, a student gives up their seat. Moving between the lobby and the
// game keeps both. Either call stops this client's room traffic itself.
onBeforeRouteLeave(async (to) => {
  if (CLASSROOM_ROUTE_NAMES.has(to.name as string)) {
    // The room's status is how everyone else learns the host has stopped running
    // a song, so the host reaching the lobby has to land in room state whichever
    // exit took them there — the back arrow, the results overlay, browser or
    // hardware back. Awaited: on a snapshot that still shows a live run, the
    // lobby view's own watcher sends the host straight back here. Guarded on the
    // status so a second pass over this guard doesn't broadcast twice.
    if (isHost.value && to.name === CLASSROOM_LOBBY_ROUTE && !belongsInLobby()) {
      exitConfirmKind.value = 'host-return-to-lobby';
      if (!guardExit(to)) return false;
      try {
        await returnToLobby();
      } catch (error) {
        console.error('[Classroom] Failed to return the room to the lobby:', error);
      }
    }
    return true;
  }

  exitConfirmKind.value = isHost.value ? 'host-leave' : 'student-leave';
  if (!guardExit(to)) return false;

  if (isHost.value) {
    isHostLeavingToNonLobby.value = true;
    await closeRoomAsHost();
  } else {
    await leaveRoom();
  }
  return true;
});

// Loop section handlers
const handleLoopUpdate = async (data: { startMeasure: number | null; endMeasure: number | null; source: LoopSource }) => {
  if (!isHost.value) return;
  const wasLoopEnabled = room.value?.loopSource !== undefined;
  const isLoopEnabled = data.source !== undefined;

  try {
    await updateLoopSection(data.startMeasure, data.endMeasure, data.source);
    if (wasLoopEnabled !== isLoopEnabled) {
      const loopStartMs = gameEngine.getLoopPreRollStartMs();
      const targetSongTimeMs = isLoopEnabled && loopStartMs !== null
        ? Math.max(0, loopStartMs)
        : Math.max(0, getCurrentSongTimeMs());

      if (isLoopEnabled && loopStartMs !== null) {
        gameEngine.seekTo(targetSongTimeMs);
      }

      await pauseAndBroadcastAtSongTime(targetSongTimeMs);
    }
  } catch (error) {
    console.error('Failed to update room loop section:', error);
    createToast({ type: 'error', message: 'Failed to update loop section' });
  }
};

// Cleanup
onUnmounted(() => {
  offPlaybackStart.off();
  offPlaybackPause.off();
  offPlaybackResume.off();
  offReturnToLobby.off();
  offGameEnd.off();
  offLoopSectionUpdate.off();
  clearCountdownTimer();
  clearPostStartResyncTimer();
  clearSeekBroadcastTimer();
  pendingSeekBroadcastMs = null;
  stopPausedSeekLerp();
  pausedSeekTargetMs = null;
  document.removeEventListener('visibilitychange', onVisibilityChange);
  window.removeEventListener('pageshow', handleAppForegroundSync);
  window.removeEventListener('focus', handleAppForegroundSync);
});
</script>

<style scoped>
.connection-indicator-wrap {
  position: absolute;
  left: 30px;
  bottom: 20px;
  z-index: 30;
}

/* Fades with the game's status/control bars — see useGameChromeVisibility. */
.chrome-fade {
  transition: opacity 0.25s ease;
}

.chrome-fade--hidden {
  opacity: 0;
  pointer-events: none;
}
</style>
